import { ACCEPT_VIDEO, posterFileErrorMessages } from '../constants';

const MAX_CROP_SIZE = 720;
const TYPE_POST_IMAGE = 'image/webp';

const once = (el: EventTarget, name: string) =>
  new Promise<void>((resolve, reject) => {
    const onOk = () => {
      cleanup();
      resolve();
    };
    const onNg = () => {
      cleanup();
      reject(new Error(`${posterFileErrorMessages.failedToCreateEvent}: ${name}`));
    };
    const cleanup = () => {
      el.removeEventListener(name, onOk);
      el.removeEventListener('error', onNg);
    };

    el.addEventListener(name, onOk, { once: true });
    el.addEventListener('error', onNg, { once: true });
  });

  const fitIn = (h: number, w: number) => {
    const max = Math.max(h, w);
    if (max <= MAX_CROP_SIZE) return { h, w };
    const s = MAX_CROP_SIZE / max;
    return { h: Math.round(h * s), w: Math.round(w * s) };
  };

  const toWebpFile = (canvas: HTMLCanvasElement) =>
    new Promise<File>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(new File([blob], 'poster-image.webp', { type: TYPE_POST_IMAGE }))
            : reject(new Error(posterFileErrorMessages.failedToCreateBlob)),
        TYPE_POST_IMAGE,
      );
    });

const getPosterFile = async (url: string, type: string): Promise<File> => {
  if (!ACCEPT_VIDEO.includes(type) && !type.startsWith('video/')) {
    throw new Error(posterFileErrorMessages.unsupportedVideoType);
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(posterFileErrorMessages.failedToGetCanvasContext);

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.src = url;

  try {
    await once(video, 'loadedmetadata');

    const { h, w } = fitIn(video.videoHeight, video.videoWidth);
    canvas.width = w;
    canvas.height = h;

    const t = Number.isFinite(video.duration)
      ? Math.min(1, Math.max(0, video.duration - 0.1))
      : 0;

    video.currentTime = t;
    await once(video, 'seeked');

    ctx.drawImage(video, 0, 0, w, h);
    return await toWebpFile(canvas);
  } finally {
    try {
      video.pause();
    } catch {}
    video.removeAttribute('src');
    video.load();
  }
};

export default getPosterFile;